import { useState, useEffect } from 'react'
import { useQuery } from 'react-query'
import { useNavigate } from 'react-router-dom'
import { 
  BarChart3, TrendingUp, Users, FileText, Search, 
  Activity, Download, Calendar, RefreshCw, AlertCircle, ArrowLeft 
} from 'lucide-react'
import { format, subDays } from 'date-fns'
import { de } from 'date-fns/locale'
import { api } from '../api/client'
import { LoadingSpinner } from '../components/LoadingSpinner'
import { ErrorHandler } from '../utils/errorHandler'
import {
  LineChart, Line, AreaChart, Area, BarChart, Bar,
  PieChart, Pie, Cell, ResponsiveContainer,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend
} from 'recharts'

const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899']

export function AnalyticsPage() {
  const navigate = useNavigate()
  const [timeRange, setTimeRange] = useState(7) // Days
  const [activeTab, setActiveTab] = useState<'overview' | 'search' | 'documents' | 'performance'>('overview')
  
  // Fetch analytics data
  const { data, isLoading, error, refetch } = useQuery(
    ['analytics', timeRange],
    () => api.admin.analyticsOverview(timeRange),
    {
      refetchInterval: 60000, // Refresh every minute
      staleTime: 30000,
    }
  )

  // Fetch real-time data
  const { data: realtimeData } = useQuery(
    'analytics-realtime',
    api.admin.analyticsRealtime,
    {
      refetchInterval: 5000, // Refresh every 5 seconds
    }
  )

  if (isLoading) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <div className="glass p-8 max-w-md text-center">
          <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
          <p className="text-white">Fehler beim Laden der Analytics</p>
          <button onClick={() => refetch()} className="btn btn-primary mt-4">
            Erneut versuchen
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="flex-1 p-6 space-y-6 overflow-y-auto">
      {/* Header */}
      <div className="glass p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl font-bold text-white mb-2">Analytics Dashboard</h1>
            <p className="text-gray-400">
              Übersicht über Nutzung und Performance
            </p>
          </div>
          
          <div className="flex items-center space-x-4">
            {/* Back Button */}
            <button
              onClick={() => navigate('/management')}
              className="flex items-center space-x-2 px-4 py-2 glass rounded-lg hover:bg-white/10"
            >
              <ArrowLeft className="w-4 h-4" />
              <span>Zurück</span>
            </button>
            
            {/* Time Range Selector */}
            <select
              value={timeRange}
              onChange={(e) => setTimeRange(Number(e.target.value))}
              className="glass px-4 py-2 rounded-lg text-white"
            >
              <option value={1}>Heute</option>
              <option value={7}>Letzte 7 Tage</option>
              <option value={30}>Letzte 30 Tage</option>
              <option value={90}>Letzte 90 Tage</option>
            </select>
            
            {/* Refresh Button */}
            <button
              onClick={() => refetch()}
              className="glass p-2 rounded-lg hover:bg-white/10"
            >
              <RefreshCw className="w-5 h-5" />
            </button>
            
            {/* Export Button */}
            <button
              onClick={() => handleExport()}
              className="btn btn-primary flex items-center space-x-2"
            >
              <Download className="w-4 h-4" />
              <span>Exportieren</span>
            </button>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="flex space-x-1 bg-black/20 p-1 rounded-lg">
          {[
            { id: 'overview', label: 'Übersicht', icon: BarChart3 },
            { id: 'search', label: 'Suche', icon: Search },
            { id: 'documents', label: 'Dokumente', icon: FileText },
            { id: 'performance', label: 'Performance', icon: Activity },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as any)}
              className={`flex-1 flex items-center justify-center space-x-2 py-2 px-4 rounded-lg transition-all ${
                activeTab === tab.id
                  ? 'bg-white/20 text-white'
                  : 'text-gray-400 hover:text-white hover:bg-white/10'
              }`}
            >
              <tab.icon className="w-4 h-4" />
              <span>{tab.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Real-time Stats */}
      {realtimeData && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <StatCard
            title="Aktive Nutzer"
            value={realtimeData.active_users.last_15_min}
            subtitle="in den letzten 15 Minuten"
            icon={Users}
            trend={calculateTrend(
              realtimeData.active_users.last_hour,
              realtimeData.active_users.last_15_min
            )}
          />
          
          <StatCard
            title="Suchanfragen"
            value={realtimeData.event_counts.last_hour.search_performed || 0}
            subtitle="in der letzten Stunde"
            icon={Search}
          />
          
          <StatCard
            title="Chat-Nachrichten"
            value={realtimeData.event_counts.last_hour.user_chat || 0}
            subtitle="in der letzten Stunde"
            icon={BarChart3}
          />
          
          <StatCard
            title="Systemlast"
            value={realtimeData.current_load.level}
            subtitle={`${realtimeData.current_load.events_per_minute.toFixed(1)} Events/Min`}
            icon={Activity}
            valueClass={
              realtimeData.current_load.level === 'high' ? 'text-red-400' :
              realtimeData.current_load.level === 'medium' ? 'text-yellow-400' :
              'text-green-400'
            }
          />
        </div>
      )}

      {/* Tab Content */}
      <div className="space-y-6">
        {activeTab === 'overview' && data && (
          <OverviewTab data={data} />
        )}
        
        {activeTab === 'search' && data && (
          <SearchTab data={data.search} />
        )}
        
        {activeTab === 'documents' && data && (
          <DocumentsTab data={data.documents} />
        )}
        
        {activeTab === 'performance' && data && (
          <PerformanceTab data={data.performance} />
        )}
      </div>
    </div>
  )

  async function handleExport() {
    try {
      const result = await api.admin.analyticsExport(activeTab, 'json')
      
      // Create download
      const blob = new Blob([JSON.stringify(result.data, null, 2)], {
        type: 'application/json'
      })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = result.filename || `analytics_${activeTab}_${format(new Date(), 'yyyyMMdd')}.json`
      a.click()
      URL.revokeObjectURL(url)
      
      ErrorHandler.showSuccess('Export erfolgreich')
    } catch (error) {
      ErrorHandler.handleApiError(error)
    }
  }
}

// Stat Card Component
function StatCard({
  title,
  value,
  subtitle,
  icon: Icon,
  trend,
  valueClass = 'text-white'
}: {
  title: string
  value: number | string
  subtitle: string
  icon: any
  trend?: number
  valueClass?: string
}) {
  return (
    <div className="glass p-6">
      <div className="flex items-start justify-between mb-4">
        <div className="p-2 bg-white/10 rounded-lg">
          <Icon className="w-6 h-6 text-blue-400" />
        </div>
        {trend !== undefined && (
          <div className={`flex items-center space-x-1 text-sm ${
            trend > 0 ? 'text-green-400' : trend < 0 ? 'text-red-400' : 'text-gray-400'
          }`}>
            <TrendingUp className={`w-4 h-4 ${trend < 0 ? 'rotate-180' : ''}`} />
            <span>{Math.abs(trend)}%</span>
          </div>
        )}
      </div>
      
      <div className={`text-3xl font-bold mb-1 ${valueClass}`}>
        {typeof value === 'number' ? value.toLocaleString('de-DE') : value}
      </div>
      
      <div>
        <div className="text-white">{title}</div>
        <div className="text-sm text-gray-400">{subtitle}</div>
      </div>
    </div>
  )
}

// Overview Tab
function OverviewTab({ data }: { data: any }) {
  const { usage, search, documents } = data

  return (
    <div className="space-y-6">
      {/* Usage Timeline */}
      <div className="glass p-6">
        <h3 className="text-xl font-semibold text-white mb-4">Nutzungsverlauf</h3>
        <div className="h-80">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={usage.time_series}>
              <defs>
                <linearGradient id="colorEvents" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.8}/>
                  <stop offset="95%" stopColor="#3b82f6" stopOpacity={0}/>
                </linearGradient>
                <linearGradient id="colorUsers" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#10b981" stopOpacity={0.8}/>
                  <stop offset="95%" stopColor="#10b981" stopOpacity={0}/>
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
              <XAxis 
                dataKey="timestamp" 
                stroke="#9ca3af"
                tickFormatter={(value) => format(new Date(value), 'dd.MM', { locale: de })}
              />
              <YAxis stroke="#9ca3af" />
              <Tooltip 
                contentStyle={{ backgroundColor: '#1f2937', border: 'none' }}
                labelFormatter={(value) => format(new Date(value), 'PPp', { locale: de })}
              />
              <Area
                type="monotone"
                dataKey="events"
                stroke="#3b82f6"
                fillOpacity={1}
                fill="url(#colorEvents)"
                name="Events"
              />
              <Area
                type="monotone"
                dataKey="unique_users"
                stroke="#10b981"
                fillOpacity={1}
                fill="url(#colorUsers)"
                name="Nutzer"
              />
              <Legend />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Top Users */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="glass p-6">
          <h3 className="text-xl font-semibold text-white mb-4">Aktivste Nutzer</h3>
          <div className="space-y-3">
            {usage.user_activity.slice(0, 10).map((user: any, index: number) => (
              <div key={user.user_id} className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="w-8 h-8 rounded-full bg-blue-500/20 flex items-center justify-center text-sm">
                    {index + 1}
                  </div>
                  <div className="text-sm text-gray-300 truncate max-w-xs">
                    {user.user_id}
                  </div>
                </div>
                <div className="text-white font-medium">
                  {user.total_actions} Aktionen
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Popular Actions */}
        <div className="glass p-6">
          <h3 className="text-xl font-semibold text-white mb-4">Beliebte Aktionen</h3>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={usage.popular_actions.slice(0, 6)}
                  dataKey="count"
                  nameKey="event_type"
                  cx="50%"
                  cy="50%"
                  outerRadius={80}
                  label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                >
                  {usage.popular_actions.slice(0, 6).map((entry: any, index: number) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip contentStyle={{ backgroundColor: '#1f2937', border: 'none' }} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
    </div>
  )
}

// Search Tab
function SearchTab({ data }: { data: any }) {
  if (!data) return null

  return (
    <div className="space-y-6">
      {/* Search Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="glass p-6">
          <div className="text-3xl font-bold text-white mb-2">
            {data.total_searches?.toLocaleString('de-DE') || 0}
          </div>
          <div className="text-gray-400">Gesamte Suchanfragen</div>
        </div>
        
        <div className="glass p-6">
          <div className="text-3xl font-bold text-white mb-2">
            {data.search_success_rate?.toFixed(1) || 0}%
          </div>
          <div className="text-gray-400">Erfolgsquote</div>
        </div>
        
        <div className="glass p-6">
          <div className="text-3xl font-bold text-red-400 mb-2">
            {data.zero_result_queries || 0}
          </div>
          <div className="text-gray-400">Suchen ohne Ergebnis</div>
        </div>
      </div>

      {/* Search Timeline */}
      {data.queries_by_day && (
        <div className="glass p-6">
          <h3 className="text-xl font-semibold text-white mb-4">Suchanfragen pro Tag</h3>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data.queries_by_day}>
                <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                <XAxis 
                  dataKey="date" 
                  stroke="#9ca3af"
                  tickFormatter={(value) => format(new Date(value), 'dd.MM', { locale: de })}
                />
                <YAxis stroke="#9ca3af" />
                <Tooltip 
                  contentStyle={{ backgroundColor: '#1f2937', border: 'none' }}
                  labelFormatter={(value) => format(new Date(value), 'PP', { locale: de })}
                />
                <Bar dataKey="count" fill="#3b82f6" name="Suchanfragen" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Popular Queries */}
      <div className="glass p-6">
        <h3 className="text-xl font-semibold text-white mb-4">Häufigste Suchanfragen</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {Object.entries(data.popular_queries || {}).slice(0, 20).map(([query, count]) => (
            <div key={query} className="flex items-center justify-between py-2 border-b border-white/10">
              <span className="text-gray-300 truncate mr-4">{query}</span>
              <span className="text-white font-medium">{count as number}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

// Documents Tab
function DocumentsTab({ data }: { data: any }) {
  if (!data) return null

  return (
    <div className="space-y-6">
      {/* Document Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="glass p-6">
          <div className="text-3xl font-bold text-white mb-2">
            {data.total_documents || 0}
          </div>
          <div className="text-gray-400">Gesamte Dokumente</div>
        </div>
        
        <div className="glass p-6">
          <div className="text-3xl font-bold text-white mb-2">
            {data.documents_uploaded || 0}
          </div>
          <div className="text-gray-400">Hochgeladen (Zeitraum)</div>
        </div>
        
        <div className="glass p-6">
          <div className="text-3xl font-bold text-white mb-2">
            {data.documents_viewed || 0}
          </div>
          <div className="text-gray-400">Angesehen (Zeitraum)</div>
        </div>
      </div>

      {/* Document Types */}
      {data.document_types && Object.keys(data.document_types).length > 0 && (
        <div className="glass p-6">
          <h3 className="text-xl font-semibold text-white mb-4">Dokumenttypen</h3>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={Object.entries(data.document_types).map(([type, count]) => ({
                    name: type,
                    value: count
                  }))}
                  dataKey="value"
                  nameKey="name"
                  cx="50%"
                  cy="50%"
                  outerRadius={80}
                  label
                >
                  {Object.entries(data.document_types).map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip contentStyle={{ backgroundColor: '#1f2937', border: 'none' }} />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Most Viewed Documents */}
      {data.most_viewed_documents && data.most_viewed_documents.length > 0 && (
        <div className="glass p-6">
          <h3 className="text-xl font-semibold text-white mb-4">Meistgesehene Dokumente</h3>
          <div className="space-y-3">
            {data.most_viewed_documents.map((doc: any) => (
              <div key={doc.document_id} className="flex items-center justify-between py-2 border-b border-white/10">
                <div className="text-gray-300 truncate mr-4">
                  {doc.title || doc.document_id}
                </div>
                <div className="text-white font-medium">
                  {doc.views} Aufrufe
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

// Performance Tab
function PerformanceTab({ data }: { data: any }) {
  if (!data) return null

  const healthColor = 
    data.health_indicators?.overall_health === 'critical' ? 'text-red-400' :
    data.health_indicators?.overall_health === 'warning' ? 'text-yellow-400' :
    'text-green-400'

  return (
    <div className="space-y-6">
      {/* System Health */}
      <div className="glass p-6">
        <h3 className="text-xl font-semibold text-white mb-4">System Health</h3>
        <div className="flex items-center space-x-4 mb-4">
          <div className={`text-2xl font-bold ${healthColor}`}>
            {data.health_indicators?.overall_health || 'Unknown'}
          </div>
          {data.health_indicators?.warnings && data.health_indicators.warnings.length > 0 && (
            <div className="flex-1">
              {data.health_indicators.warnings.map((warning: string, index: number) => (
                <div key={index} className="text-yellow-400 text-sm">
                  ⚠️ {warning}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Performance Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* API Performance */}
        {data.api_performance && (
          <div className="glass p-6">
            <h3 className="text-xl font-semibold text-white mb-4">API Performance</h3>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-gray-400">Durchschnittliche Antwortzeit</span>
                <span className="text-white font-medium">
                  {data.api_performance.avg_response_time?.toFixed(0) || 0}ms
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-400">95. Perzentil</span>
                <span className="text-white font-medium">
                  {data.api_performance.p95_response_time?.toFixed(0) || 0}ms
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-400">99. Perzentil</span>
                <span className="text-white font-medium">
                  {data.api_performance.p99_response_time?.toFixed(0) || 0}ms
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-400">Gesamte Anfragen</span>
                <span className="text-white font-medium">
                  {data.api_performance.total_requests?.toLocaleString('de-DE') || 0}
                </span>
              </div>
            </div>
          </div>
        )}

        {/* Cache Performance */}
        {data.cache_performance && (
          <div className="glass p-6">
            <h3 className="text-xl font-semibold text-white mb-4">Cache Performance</h3>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-gray-400">Hit Rate</span>
                <span className={`font-medium ${
                  data.cache_performance.hit_rate > 80 ? 'text-green-400' :
                  data.cache_performance.hit_rate > 60 ? 'text-yellow-400' :
                  'text-red-400'
                }`}>
                  {data.cache_performance.hit_rate?.toFixed(1) || 0}%
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-400">Cache Hits</span>
                <span className="text-white font-medium">
                  {data.cache_performance.total_hits?.toLocaleString('de-DE') || 0}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-400">Cache Misses</span>
                <span className="text-white font-medium">
                  {data.cache_performance.total_misses?.toLocaleString('de-DE') || 0}
                </span>
              </div>
            </div>
            
            {/* Hit Rate Visualization */}
            <div className="mt-4">
              <div className="h-3 bg-black/30 rounded-full overflow-hidden">
                <div 
                  className="h-full bg-gradient-to-r from-blue-500 to-green-500 transition-all duration-500"
                  style={{ width: `${data.cache_performance.hit_rate || 0}%` }}
                />
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Error Rate */}
      <div className="glass p-6">
        <h3 className="text-xl font-semibold text-white mb-4">Fehlerrate</h3>
        <div className="flex items-center space-x-4">
          <div className={`text-3xl font-bold ${
            data.error_rate > 5 ? 'text-red-400' :
            data.error_rate > 2 ? 'text-yellow-400' :
            'text-green-400'
          }`}>
            {data.error_rate?.toFixed(2) || 0}%
          </div>
          <div className="text-gray-400">
            der Anfragen führten zu Fehlern
          </div>
        </div>
      </div>
    </div>
  )
}

// Helper Functions
function calculateTrend(oldValue: number, newValue: number): number {
  if (oldValue === 0) return 0
  return Math.round(((newValue - oldValue) / oldValue) * 100)
}